import { Observable, of, Subscription } from 'rxjs';
import { Store } from '@ngxs/store';
import { switchMap } from 'rxjs/operators';
import { SelectionModel } from '@angular/cdk/collections';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { ChangeDetectionStrategy, Component, OnInit, input, output, inject } from '@angular/core';
import { AutoUnsubscribe } from '../../../shared/utils';
import { InitialDataStateModel, Language, LanguageGroup, SectionalLanguageList } from '../../../model';
import { mainLanguageCodesArray, MANDATORY_LANGUAGE } from '../../../config';
import { InitialDataState } from '../../state';

@Component({
  selector: 'app-language-selector',
  templateUrl: './language-selector.component.html',
  styleUrls: ['./language-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatCheckbox],
})
@AutoUnsubscribe
export class LanguageSelectorComponent implements OnInit {
  readonly selectedLanguages = input<Array<string> | Observable<Array<string>>>(undefined);
  readonly languageSelectionChanged = output<SectionalLanguageList>();

  subscription: Subscription;
  mandatoryLanguage = MANDATORY_LANGUAGE;
  languages: { main: Array<Language>; additional: Array<Language> } = {
    main: [],
    additional: [],
  };
  selection: { main: SelectionModel<Language>; additional: SelectionModel<Language> } = {
    main: new SelectionModel<Language>(true, []),
    additional: new SelectionModel<Language>(true, []),
  };

  initialData$: Observable<InitialDataStateModel> = inject(Store).select(InitialDataState.initialData);

  ngOnInit(): void {
    this.subscription = this.initialData$
      .pipe(
        switchMap((response: InitialDataStateModel) => {
          this.languages = {
            main: mainLanguageCodesArray.map(lnCode => response.languageList.find(language => language?.lnCode === lnCode)),
            additional: response.languageList.filter(language => mainLanguageCodesArray.indexOf(language.lnCode) < 0),
          };
          const selectedLanguages = this.selectedLanguages();
          return selectedLanguages instanceof Observable ? selectedLanguages : of(selectedLanguages);
        })
      )
      .subscribe({
        next: (displayingLanguages: Array<string>) => {
          displayingLanguages.forEach(lnCode => {
            if (this.languages.main.findIndex(language => language.lnCode === lnCode) >= 0) {
              this.selection.main.select(this.languages.main.find(language => language.lnCode === lnCode));
            }
            if (this.languages.additional.findIndex(language => language.lnCode === lnCode) >= 0) {
              this.selection.additional.select(this.languages.additional.find(language => language.lnCode === lnCode));
            }
          });
        },
      });
  }

  isAllSelected(section: LanguageGroup): boolean {
    return this.selection[section].selected.length === this.languages[section].length;
  }

  masterToggle(section: LanguageGroup): void {
    if (this.isAllSelected(section)) {
      this.selection[section].clear();
    } else {
      this.languages[section].forEach(row => this.selection[section].select(row));
    }
    // 'en' should always be selected.
    this.selection.main.select(this.languages.main.find(language => language.lnCode === this.mandatoryLanguage));
    this.languageSelectionChanged.emit({ main: this.selection.main.selected, additional: this.selection.additional.selected });
  }

  onSelectionChange(event: MatCheckboxChange, language: Language, section: LanguageGroup): void {
    if (event) {
      this.selection[section].toggle(language);
    }
    this.languageSelectionChanged.emit({
      main: this.selection.main.selected,
      additional: this.selection.additional.selected,
    });
  }
}
